import {
  PaginatedRequestParams,
  PaginatedResponse,
  RtkEmptySplitApi,
} from "../../types";
import { WithApiRequestMeta } from "../../api-results";
import { FormField } from "../customer-form-field/types";
import { CreateSubtaskDto } from "../tasks/models/Subtask/SubtaskDto";
import { TaskCustomFieldDto } from "../tasks/models/TaskCustomField/TaskCustomFieldDto";
import {
  InvoiceGuestLogDto,
  transformInvoiceGuestLogDtoToModel,
} from "./models/InvoiceGuestLog";
import {
  OrderListItemDto,
  OrderListItemModel,
  transformOrderListItemDtoToModel,
} from "./models/OrderListItem";
import {
  OrderResponseDto,
  OrderResponseModel,
  transformOrderResponseDtoToModel,
} from "./models/OrderResponse";
import { OrderTaskDto, OrderTaskModel } from "./models/OrderTask";
import { transformOrderTaskDtoToModel } from "./models/OrderTask/utils";
import { OrdersApiTags } from "./tags";
import {
  CreateOrderFormPayload,
  OrderForm,
  OrderResponseField,
  OrderTaskListSearchBy,
  OrderTaskListSortBy,
  OrderResponseFilterBy,
} from "./types";

export const addOrdersApi = (emptySplitApi: RtkEmptySplitApi) => {
  const apiWithTags = emptySplitApi.enhanceEndpoints({
    addTagTypes: [OrdersApiTags.orderForm, OrdersApiTags.orderResponse],
  });

  const ordersApi = apiWithTags.injectEndpoints({
    overrideExisting: false,
    endpoints: (builder) => ({
      // #region Order forms
      getOrderForms: builder.query<
        PaginatedResponse<OrderListItemModel>,
        WithApiRequestMeta<
          PaginatedRequestParams<{
            entityId: string;
            form_type?: "internal" | "external";
            board_id?: string;
            // Extra param to handle tag invalidation only for tabs in the Tasks Forms View
            isFormTabs?: boolean;
          }>
        >
      >({
        query: ({ entityId, isFormTabs, meta: _meta, ...pageParams }) => ({
          url: `/api/auth/${entityId}/order/form`,
          params: {
            ...pageParams,
          },
        }),
        transformResponse: (response: PaginatedResponse<OrderListItemDto>) => ({
          ...response,
          data: response.data.map(transformOrderListItemDtoToModel),
        }),
        providesTags: (_result, _error, args) =>
          args.isFormTabs
            ? [{ type: OrdersApiTags.orderForm, id: "FORM_TABS" }]
            : [],
      }),

      getOrderFormsByDiscountId: builder.query<
        PaginatedResponse<OrderListItemModel>,
        WithApiRequestMeta<
          PaginatedRequestParams<{ entityId: string; discountId: string }>
        >
      >({
        query: ({ entityId, discountId, meta: _meta, ...params }) => ({
          url: `/api/auth/${entityId}/order/form`,
          params: {
            discount_id: discountId,
            ...params,
          },
        }),
        transformResponse: (response: PaginatedResponse<OrderListItemDto>) => ({
          ...response,
          data: response.data.map(transformOrderListItemDtoToModel),
        }),
      }),

      getOrderForm: builder.query<
        OrderForm,
        WithApiRequestMeta<{
          entityId: string;
          formId: string;
        }>
      >({
        query: ({ entityId, formId }) => ({
          url: `/api/auth/${entityId}/order/form/${formId}`,
        }),
        providesTags: (e1, e2, { formId }) => [
          { type: OrdersApiTags.orderForm, id: formId },
        ],
      }),

      createOrderForm: builder.mutation<
        OrderListItemModel,
        WithApiRequestMeta<{
          entityId: string;
          opts: CreateOrderFormPayload;
          banner?: File;
        }>
      >({
        query: ({ entityId, opts, banner }) => {
          const dataForm = new FormData();
          dataForm.set("opts", JSON.stringify(opts));
          if (banner) {
            dataForm.set("banner_img_file", banner);
          }

          return {
            url: `/api/auth/${entityId}/order/form`,
            method: "POST",
            data: dataForm,
          };
        },
        transformResponse: (response: OrderListItemDto) =>
          transformOrderListItemDtoToModel(response),
        invalidatesTags: [{ type: OrdersApiTags.orderForm, id: "FORM_TABS" }],
      }),

      updateOrderForm: builder.mutation<
        void,
        WithApiRequestMeta<{
          entityId: string;
          formId: string;
          opts: Partial<CreateOrderFormPayload>;
          banner?: File;
        }>
      >({
        query: ({ entityId, formId, opts, banner }) => {
          const dataForm = new FormData();
          dataForm.set("opts", JSON.stringify(opts));
          if (banner) {
            dataForm.set("banner_img_file", banner);
          }

          return {
            url: `/api/auth/${entityId}/order/form/${formId}`,
            method: "PUT",
            data: dataForm,
          };
        },
        invalidatesTags: (e1, e2, { formId }) => [
          { type: OrdersApiTags.orderForm, id: formId },
          { type: OrdersApiTags.orderForm, id: "FORM_TABS" },
        ],
      }),

      deleteOrderFormFile: builder.mutation<
        void,
        WithApiRequestMeta<{
          entityId: string;
          fileId: string;
        }>
      >({
        query: ({ entityId, fileId }) => ({
          url: `/api/auth/drive/entity/${entityId}/${fileId}`,
          method: "DELETE",
        }),
      }),

      deleteOrderForm: builder.mutation<
        void,
        WithApiRequestMeta<{ entityId: string; formId: string }>
      >({
        query: ({ entityId, formId }) => ({
          url: `/api/auth/${entityId}/order/form/${formId}`,
          method: "DELETE",
        }),
        invalidatesTags: [{ type: OrdersApiTags.orderForm, id: "FORM_TABS" }],
      }),

      createOrder: builder.mutation<
        { order_resp: string },
        WithApiRequestMeta<{
          entityId: string;
          taskId: string;
          boardId: string | null;
        }>
      >({
        query: ({ entityId, taskId, boardId }) => ({
          url: `/api/auth/${entityId}/order/response`,
          method: "POST",
          data: {
            task_id: taskId,
            board_id: boardId,
          },
        }),
      }),

      getOrderResponse: builder.query<
        OrderResponseModel,
        WithApiRequestMeta<{
          entityId: string;
          orderResponseId: string;
        }>
      >({
        query: ({ entityId, orderResponseId }) => ({
          url: `/api/auth/${entityId}/order/response/${orderResponseId}`,
        }),
        transformResponse: (response: OrderResponseDto) =>
          transformOrderResponseDtoToModel(response),
        providesTags: (e1, e2, { orderResponseId }) => [
          { type: OrdersApiTags.orderResponse, id: orderResponseId },
        ],
      }),

      updateOrderResponseField: builder.mutation<
        OrderResponseField[],
        WithApiRequestMeta<{
          entityId: string;
          orderResponseId: string;
          field: OrderResponseField;
        }>
      >({
        query: ({ entityId, orderResponseId, field }) => ({
          url: `/api/auth/${entityId}/order/response/${orderResponseId}`,
          method: "PUT",
          data: { field },
        }),
        transformResponse: (response: { resp: OrderResponseField[] }) =>
          response.resp,
        invalidatesTags: (_result, _error, { orderResponseId }) => [
          { type: OrdersApiTags.orderResponse, id: orderResponseId },
        ],
      }),

      getOrderTasks: builder.query<
        PaginatedResponse<OrderTaskModel>,
        WithApiRequestMeta<
          PaginatedRequestParams<{
            entityId: string;
            boardId: string;
            formId: string;
            formPrefix: string;
            from: Date;
            to: Date;
            searchQuery: string;
            searchDropdownValueIds: string[];
            searchBy: OrderTaskListSearchBy;
            sortDirection: "asc" | "desc";
            sortBy: OrderTaskListSortBy;
            filterBy: OrderResponseFilterBy;
          }>
        >
      >({
        query: ({
          entityId,
          boardId,
          formId,
          formPrefix,
          from,
          to,
          searchQuery,
          searchDropdownValueIds,
          searchBy,
          sortDirection,
          sortBy,
          filterBy,
          meta: _meta,
          ...params
        }) => {
          const isDropdownSearch =
            searchBy.type === "dropdown" &&
            searchBy.category === "custom_field";

          const isCustomerDropdownSearch =
            searchBy.type === "customer_dropdown" &&
            searchBy.category === "form_field";

          let searchInput: string | string[] | undefined;

          // The backend's `search_input` parameter behaves differently based on the `search_by` type:
          // 1. For dropdown-type task custom fields (category: "custom_field", type: "dropdown"),
          //    it expects an array of matched dropdown option IDs.
          // 2. For customer dropdown fields (category: "form_field", type: "customer_dropdown"),
          //    it expects an array of matched dropdown option IDs.
          // 3. For all other searches (including form response fields), it expects a string with wildcards.
          if (searchQuery) {
            if (isDropdownSearch || isCustomerDropdownSearch) {
              searchInput = searchDropdownValueIds;
            } else {
              searchInput = `%${searchQuery}%`;
            }
          }

          return {
            url: `/api/auth/${entityId}/order/list`,
            method: "POST",
            data: {
              board_id: boardId,
              form_id: formId,
              prefix: formPrefix,
              from: from.toISOString(),
              to: to.toISOString(),
              search_input: searchInput,
              search_by: searchQuery ? searchBy : undefined,
              sort_dir: sortDirection,
              sort_by: sortBy,
              filter_by: filterBy,
              ...params,
            },
          };
        },
        transformResponse: (response: PaginatedResponse<OrderTaskDto>) => ({
          ...response,
          data: response.data.map(transformOrderTaskDtoToModel),
        }),
      }),
      // #endregion

      // #region Guest order forms
      getGuestOrderResponse: builder.query<
        OrderResponseModel,
        WithApiRequestMeta<{
          entityId: string;
          orderResponseId: string;
        }>
      >({
        query: ({ entityId, orderResponseId }) => ({
          url: `/api/guest/${entityId}/order/response/${orderResponseId}`,
        }),
        transformResponse: (response: OrderResponseDto) =>
          transformOrderResponseDtoToModel(response),
      }),

      guestOrderForm: builder.query<
        OrderForm,
        WithApiRequestMeta<{ entityId: string; formId: string }>
      >({
        query: ({ entityId, formId }) => ({
          url: `/api/guest/${entityId}/order/form/${formId}`,
        }),
      }),

      guestInit: builder.query<
        {
          custom_fields: { props: { value: TaskCustomFieldDto[] } };
          customer_form_fields: { data: FormField[] };
          entity: { id: string; name: string; currency: string };
        },
        WithApiRequestMeta<{ entityId: string }>
      >({
        query: ({ entityId }) => ({
          url: `/api/guest/${entityId}/order/init`,
        }),
      }),

      guestSubmitOrder: builder.mutation<
        void,
        WithApiRequestMeta<{
          entityId: string;
          formId: string;
          isAuthenticated?: boolean;
          data: {
            name: string;
            priority: string;
            due_at: string | null;
            fields?: Array<{ [key: string]: string }>;
            cust_id?: string;
            custom_fields?: { [key: string]: string };
            response?: {
              id: string;
              name: string;
              value: string;
              type: string;
            }[];
            items?: unknown[];
            has_chklist_chk?: boolean;
            checklist: CreateSubtaskDto[];
            captcha?: {
              grc_token: string;
              agent?: string;
            };
          };
          files?: Record<string, File[]>;
        }>
      >({
        query: ({ entityId, formId, data, isAuthenticated, files }) => {
          const formData = new FormData();
          formData.set("payload", JSON.stringify(data));
          if (files) {
            Object.entries(files).forEach(([fieldId, fieldFiles]) => {
              fieldFiles.forEach((file) => {
                formData.append(`files[${fieldId}][]`, file);
              });
            });
          }

          return {
            url: isAuthenticated
              ? `/api/auth/${entityId}/order/form/${formId}/submit`
              : `/api/guest/${entityId}/order/form/${formId}/submit`,
            method: "POST",
            data: formData,
          };
        },
      }),
      // #endregion

      // #region Order items
      addOrderLineItems: builder.mutation<
        void,
        WithApiRequestMeta<{
          entityId: string;
          invoiceId: string;
          items: {
            inventory_id: string;
            qty: number;
          }[];
          orderResponseId: string;
        }>
      >({
        query: ({ entityId, items, invoiceId }) => ({
          url: `/api/auth/${entityId}/order/lineitem`,
          data: {
            items,
            invoice_id: invoiceId,
          },
          method: "POST",
        }),
        invalidatesTags: (e1, e2, { orderResponseId }) => [
          {
            type: OrdersApiTags.orderResponse,
            id: orderResponseId,
          },
        ],
      }),

      deleteOrderLineItem: builder.mutation<
        void,
        WithApiRequestMeta<{
          entityId: string;
          invoiceId: string;
          inventoryId: string[];
          orderResponseId: string;
        }>
      >({
        query: ({ entityId, invoiceId, inventoryId }) => ({
          url: `/api/auth/${entityId}/order/lineitem`,
          data: {
            inventory_id: inventoryId,
            invoice_id: invoiceId,
          },
          method: "DELETE",
        }),
        invalidatesTags: (e1, e2, { orderResponseId }) => [
          {
            type: OrdersApiTags.orderResponse,
            id: orderResponseId,
          },
        ],
      }),

      updateLineItems: builder.mutation<
        void,
        WithApiRequestMeta<{
          entityId: string;
          invoiceId: string;
          items: {
            inventory_id: string;
            qty?: number;
            price?: number;
          }[];
          orderResponseId: string;
        }>
      >({
        query: ({ entityId, items, invoiceId }) => ({
          url: `/api/auth/${entityId}/order/lineitem`,
          data: {
            items,
            invoice_id: invoiceId,
          },
          method: "PUT",
        }),
        invalidatesTags: (e1, e2, { orderResponseId }) => [
          {
            type: OrdersApiTags.orderResponse,
            id: orderResponseId,
          },
        ],
      }),
      // #endregion

      // #region Order invoice history
      getOrderInvoiceHistory: builder.query<
        PaginatedResponse<InvoiceGuestLogDto>,
        WithApiRequestMeta<
          PaginatedRequestParams<{
            entityId: string;
            invoiceId: string;
            isAuthenticated?: boolean;
          }>
        >
      >({
        query: ({
          entityId,
          invoiceId,
          isAuthenticated,
          meta: _meta,
          ...pageParams
        }) => ({
          url: isAuthenticated
            ? `/api/auth/${entityId}/order/logs/${invoiceId}`
            : `/api/guest/${entityId}/order/logs/${invoiceId}`,
          params: pageParams,
        }),
        transformResponse: (
          response: PaginatedResponse<InvoiceGuestLogDto>
        ) => ({
          ...response,
          data: response.data.map((item) =>
            transformInvoiceGuestLogDtoToModel(item)
          ),
        }),
      }),
      // #endregion
    }),
  });

  return ordersApi;
};

export type OrdersApi = ReturnType<typeof addOrdersApi>;
